import { createFileRoute, redirect } from '@tanstack/react-router';
import { POSAdminLayout } from '@/features/pos/admin/components/POSAdminLayout';
import { usePOSShopSettings, useCreateShopSettings, useUpdateShopSettings } from '@/features/pos/hooks/useShopSettings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Save, Store } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from '@/components/ui/toast/useToast';
import { ShopSettingsFormData } from '@/features/pos/types/admin';
import { useEffect } from 'react';

export const Route = createFileRoute('/apps/pos/admin/settings/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ShopSettingsPage,
});

function ShopSettingsPage() {
  const { data: settings, isLoading } = usePOSShopSettings();
  const createSettings = useCreateShopSettings();
  const updateSettings = useUpdateShopSettings();

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<ShopSettingsFormData>({
    defaultValues: {
      shop_name: '',
      shop_type: 'restaurant',
      receipt_header: '',
      receipt_footer: 'Merci de votre visite!',
      tax_number: '',
      enable_table_service: true,
      enable_takeaway: true,
      enable_delivery: false,
      enable_vat: true,
      default_vat: 19,
      fiscal_stamp_threshold: 5000,
    },
  });

  useEffect(() => {
    if (settings) {
      reset(settings);
    }
  }, [settings, reset]);

  const onSubmit = async (data: ShopSettingsFormData) => {
    try {
      if (settings) {
        await updateSettings.mutateAsync(data);
        toast.success('تم الحفظ / Saved', 'Settings updated successfully');
      } else {
        await createSettings.mutateAsync(data);
        toast.success('تم الإنشاء / Created', 'Settings created successfully');
      }
    } catch (error: any) {
      toast.error('خطأ / Error', error.message);
    }
  };

  if (isLoading) {
    return (
      <POSAdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </POSAdminLayout>
    );
  }

  return (
    <POSAdminLayout>
      <div className="p-8">
        <div className="max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Store className="w-8 h-8 text-emerald-600" />
              <h1 className="text-3xl font-bold text-foreground">Shop Settings</h1>
            </div>
            <p className="text-muted-foreground">Configure your POS shop settings and preferences</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Shop name and business type configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="shop_name">Shop Name *</Label>
                  <Input
                    id="shop_name"
                    {...register('shop_name', { required: 'Shop name is required' })}
                    placeholder="My Restaurant"
                  />
                  {errors.shop_name && (
                    <p className="text-sm text-red-600 mt-1">{errors.shop_name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="shop_type">Business Type *</Label>
                  <Select
                    value={watch('shop_type')}
                    onValueChange={(value: any) => setValue('shop_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="restaurant">🍽️ Restaurant</SelectItem>
                      <SelectItem value="cafe">☕ Cafe</SelectItem>
                      <SelectItem value="bakery">🥖 Bakery</SelectItem>
                      <SelectItem value="retail">🏪 Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tax_number">Tax Number (NIF)</Label>
                  <Input
                    id="tax_number"
                    {...register('tax_number')}
                    placeholder="123456789000123"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Service Options */}
            <Card>
              <CardHeader>
                <CardTitle>Service Options</CardTitle>
                <CardDescription>Enable or disable different service types</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <Label htmlFor="enable_table_service" className="text-base font-medium">
                      Table Service (Dine-in)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Enable table management and dine-in orders
                    </p>
                  </div>
                  <Switch
                    id="enable_table_service"
                    checked={watch('enable_table_service')}
                    onCheckedChange={(checked) => setValue('enable_table_service', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <Label htmlFor="enable_takeaway" className="text-base font-medium">
                      Takeaway
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Enable counter pickup orders
                    </p>
                  </div>
                  <Switch
                    id="enable_takeaway"
                    checked={watch('enable_takeaway')}
                    onCheckedChange={(checked) => setValue('enable_takeaway', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <Label htmlFor="enable_delivery" className="text-base font-medium">
                      Delivery
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Enable delivery orders
                    </p>
                  </div>
                  <Switch
                    id="enable_delivery"
                    checked={watch('enable_delivery')}
                    onCheckedChange={(checked) => setValue('enable_delivery', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tax Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Tax Settings</CardTitle>
                <CardDescription>Default VAT and fiscal stamp configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <Label htmlFor="enable_vat" className="text-base font-medium">
                      TVA / VAT
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Activer ou désactiver la TVA sur les ventes
                    </p>
                  </div>
                  <Switch
                    id="enable_vat"
                    checked={watch('enable_vat')}
                    onCheckedChange={(checked) => setValue('enable_vat', checked)}
                  />
                </div>

                {watch('enable_vat') && (
                <div>
                  <Label htmlFor="default_vat">Default VAT Rate (%)</Label>
                  <Input
                    id="default_vat"
                    type="number"
                    step="0.01"
                    {...register('default_vat', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Standard Algerian VAT is 19%
                  </p>
                </div>
                )}

                <div>
                  <Label htmlFor="fiscal_stamp_threshold">Fiscal Stamp Threshold (DA)</Label>
                  <Input
                    id="fiscal_stamp_threshold"
                    type="number"
                    step="0.01"
                    {...register('fiscal_stamp_threshold', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Apply 100 DA fiscal stamp for orders above this amount (standard: 5000 DA)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4">
              <Button 
                type="submit" 
                size="lg" 
                className="gap-2" 
                disabled={createSettings.isPending || updateSettings.isPending}
              >
                {(createSettings.isPending || updateSettings.isPending) ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {settings ? 'Update Settings' : 'Create Settings'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </POSAdminLayout>
  );
}
